export type GeneralReportFilterOption = {
  value: number;
  label: string;
  displayLabel: string;
};

type GeneralReportFilterOptionSource = Omit<GeneralReportFilterOption, 'displayLabel'>;
type NullableFilterValue = number | null | undefined;

const normalizeOptionLabel = (label: string, value: number): string => label.trim() || `#${value}`;

export const buildGeneralReportFilterOptions = (
  endpointOptions: readonly GeneralReportFilterOptionSource[],
  rowFallbackOptions: readonly GeneralReportFilterOptionSource[],
  locale: string
): GeneralReportFilterOption[] => {
  const labelByValue = new Map<number, string>();

  rowFallbackOptions.forEach(({ value, label }) => {
    if (!labelByValue.has(value)) {
      labelByValue.set(value, normalizeOptionLabel(label, value));
    }
  });

  endpointOptions.forEach(({ value, label }) => {
    const normalizedLabel = label.trim();

    if (normalizedLabel || !labelByValue.has(value)) {
      labelByValue.set(value, normalizedLabel || `#${value}`);
    }
  });

  const collator = new Intl.Collator(locale, { numeric: true, sensitivity: 'base' });

  const sortedOptions = Array.from(labelByValue, ([value, label]) => ({ value, label })).sort(
    (first, second) => collator.compare(first.label, second.label) || first.value - second.value
  );
  const labelOccurrences = sortedOptions.reduce<Map<string, number>>((occurrences, { label }) => {
    occurrences.set(label, (occurrences.get(label) ?? 0) + 1);
    return occurrences;
  }, new Map());

  return sortedOptions.map(({ value, label }) => ({
    value,
    displayLabel: label,
    label: labelOccurrences.get(label) === 1 ? label : `${label} (#${value})`,
  }));
};

export const createGeneralReportLookupComparator = (
  options: readonly GeneralReportFilterOption[],
  locale: string,
  missingValueLabel: string
) => {
  const labelByValue = new Map(options.map(({ value, displayLabel }) => [value, displayLabel]));
  const collator = new Intl.Collator(locale, { numeric: true, sensitivity: 'base' });
  const getLabel = (value: NullableFilterValue): string =>
    value == null ? missingValueLabel : (labelByValue.get(value) ?? `#${value}`);

  return (first: NullableFilterValue, second: NullableFilterValue): number => {
    const labelComparison = collator.compare(getLabel(first), getLabel(second));

    if (labelComparison !== 0) return labelComparison;
    if (first == null && second == null) return 0;
    if (first == null) return -1;
    if (second == null) return 1;

    return first - second;
  };
};
